import { C1_CONTRACT, C1_PROVISIONAL_STATUS_ID, applyStatusSkillLines, c1ProvisionalStatusChainId, type PossessorCompileContext } from './possessor-compile.js';
import { compileCreatureSpeeds } from './creature-speeds.js';
import { statusProjectionDependencies, statusDefinitionLayerNames } from './status-attack-budget.js';
import { storedStatusRows } from './creature-status-reader.js';
import { readItemField } from './item-field-reader.js';
import { readLayerListEntryUuid } from './inventory-raw-readers.js';
import { encounterCreatureField } from '../../shared/array-types.js';
import { createStatusLayerReadContext } from './status-row-construction.js';
import { createVitalityCompiler } from './vitality-build-context.js';
import { opportunityAttackInput } from './opportunity-attack-input.js';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity.js';
import { readItemScalarLayerSlot } from './layer-cell-readers.js';
import { surfaceStatusRuntimeOnly } from './surface-status-runtime.js';
import type { MemberStatusStep, MemberRecompileResult, MemberRecompileScene } from './member-status-recompile.js';
import { recompileFormRemoval } from './form-revert.js';
import { recompileMemberFormAddition } from './member-form-addition.js';
import { applyStatusSkillGlobals, STATUS_SKILL_GLOBAL_LAYERS, layerEntryTargetsSkill } from './creature-status-skill-globals.js';
import { createStatusIntegerCompiler } from './status-integer-contribution.js';
import { createAdvDisSourceReader } from './adv-dis-source-reader.js';
import { phpJsonInt, phpJsonString } from './php-json-values.js';
import { readInventoryRows, readInventoryField, readItemUuidSlot } from './inventory-raw-readers.js';
import { createIntegerRollupCompiler, statusIntegerFamilyComputedByWalk } from './integer-layer-rollup.js';
import { integerCompileBorrowedRow } from './integer-compute-slots.js';
import { applyStatusAcDelta } from './creature-ac-slots.js';
import { createComputeSourceCleaner } from './compute-source-cleanup.js';
import { computeSourceBreakdown } from './computed-persistence.js';
import { readStatusRowStack } from './status-stack-reader.js';
import { isClassAuraDefinition } from '../statuses/class-aura.js';
import { statusRowOnlyLayer } from '../statuses/runtime-layers.js';
import { STATUS_RESIDUAL_LAYERS, STATUS_ACTION_RESIDUAL_LAYERS } from './status-residue-layers.js';
import { compileStatusResidueFields } from './status-root-residue.js';
type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** Optional, page-owned work counters. Shared through globalThis across bundle copies; absent in ordinary play. */
export function countStatusCompileWork(kind: 'full' | 'form' | 'speeds' | 'skills' | 'vitality' | 'opportunity' | 'piloted', row: Row): void {
  const counts = (globalThis as { ftStatusCompileCounts?: Record<string, Partial<Record<typeof kind, number>>> }).ftStatusCompileCounts;
  if (!counts) return;
  const id = upper(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id'));
  if (id) { const member = counts[id] ??= { full: 0, speeds: 0 }; member[kind] = (member[kind] ?? 0) + 1; }
}

/** ID readers independent of a definition's layers: vitality's Blighted family;
 * speed's Riding/Piloted/Piloting (Piloting also changes inventory grants).
 * Quantity aliases must never be mistaken for layerless runtime markers. */
export function statusHasCompileIdDependency(definition: Row): boolean {
  const id = upper(readItemField(definition, 'item_id'));
  return C1_CONTRACT.rollup.quantityIds.some(value => upper(value) === id)
    || C1_CONTRACT.vitality.statusCatalog.rows.some(row => upper(row.item_id) === id
      && ['riding', 'piloted', 'piloting'].includes(row.variable_name));
}

const SPEED_LAYERS = new Set(['speed_walking', 'speed_climbing', 'speed_swimming', 'speed_flying', 'speed_burrowing',
  'speed_hovering', 'speed_jumping', 'speed_jumping_multiplier', 'teleport', 'speed_cannot_be_reduced']);
// Cast prerequisites and trigger metadata stay on the held source definition;
// the dependency check below still guards changes to a retained ability's condition.
// The cast already applied its target statuses; its lingering applier adds no derived cluster for that layer.
const SOURCE_LAYERS = new Set(['range', 'override_ability', 'if_armor_type_equipped_active', 'triggers_when_caster_takes_any_damage',
  'if_caster_has_status_effect_not_active', 'status_effect_applied_to_target_after_cast']);
// The aura planner owns membership/radius; these fields add no holder cluster.
// Restrict this admission to allied class auras, whose self-anchor is payload too.
const CLASS_AURA_SOURCE_LAYERS = new Set(['class_aura', 'aoe_type', 'aoe_distance', 'aoe_distance_depth', 'aoe_distance_height',
  'persistent_aoe', 'aoe_follows_target', 'effect_only_affects_same_team_type', 'requires_class', 'requires_class_level']);

/** Apply one coherent batch to the status buckets, then compile its affected cluster once.
 * null means this batch changes another cluster/dependency; the existing compiler handles it.
 * This does not cross dispatch/trigger boundaries or mutate the borrowed member. */
export function recompileMemberStatusDiff(row: Row, steps: MemberStatusStep[],
  ctx: Pick<PossessorCompileContext, 'definition' | 'catalog' | 'roster'> & MemberRecompileScene): MemberRecompileResult | null {
  const form = recompileFormRemoval(row, steps, ctx.definition);
  if (form) return form;
  const addition = recompileMemberFormAddition(row, steps, ctx.definition);
  if (addition) return addition;
  const carrier = encounterCreatureField(row.encounter_creatures, 'item_type');
  if (!steps.length || carrier !== 'player_character' && carrier !== 'creature') return null;
  const computed = isObj(row.computed) ? row.computed : {}, creature = isObj(computed.creature) ? computed.creature : null;
  let vitality = isObj(computed.vitality) ? computed.vitality : null;
  if (!creature || !vitality) return null;
  const deps = statusProjectionDependencies(row, ctx.definition);
  const readers = createStatusLayerReadContext(deps);
  const definitions = steps.map(step => ctx.definition(upper(step.statusItemId)));
  let speeds = false, skills = false, hp = false, mitigation = false, magical = false;
  const globals = new Set<string>();
  const integers = new Set<string>();
  for (const def of definitions) {
    if (surfaceStatusRuntimeOnly(def)) continue;
    if (!def || readItemField(def, 'item_type') === 'creature') return null;
    const alliedClassAura = isClassAuraDefinition(def, (_definition, key) => readers.layerValue(def, key))
      && phpJsonInt(readers.layerValue(def, 'effect_only_affects_same_team_type')) === 1;
    const names = statusDefinitionLayerNames(def);
    const integerQuantity = C1_CONTRACT.rollup.quantityIds.some(id => upper(id) === upper(readItemField(def, 'item_id')))
      && names.some(key => key === 'ac_mod_magical' || statusIntegerFamilyComputedByWalk(key, C1_CONTRACT.rollup))
      && names.every(key => statusRowOnlyLayer(key) || key === 'ac_mod_magical' || statusIntegerFamilyComputedByWalk(key, C1_CONTRACT.rollup));
    if (statusHasCompileIdDependency(def) && !integerQuantity) {
      const name = C1_CONTRACT.vitality.statusCatalog.rows.find(entry => upper(entry.item_id) === upper(readItemField(def, 'item_id')))?.variable_name;
      const blight = C1_CONTRACT.vitality.statusCatalog.quantityGroups.blighted;
      if (name === 'blighted' || name && blight?.aliases?.[name] !== undefined) hp = true;
      // Fatigue's count is read by the preparation gate; its identity adds no derived cluster.
      else if (name !== 'arcane_fatigue' && !statusDefinitionLayerNames(def).some(key => STATUS_SKILL_GLOBAL_LAYERS.some(layer => layer === key))) return null; // Other quantity families and vehicle grants retain their native dependency path.
    }
    for (const key of statusDefinitionLayerNames(def)) {
      if (SPEED_LAYERS.has(key)) speeds = true;
      else if (key === 'skill_roll_adv' || key === 'skill_roll_dis') skills = true;
      else if (STATUS_SKILL_GLOBAL_LAYERS.some(layer => layer === key)) globals.add(key);
      else if (key === 'damage_mitigation') mitigation = true;
      else if (key === 'ac_mod_magical') magical = true;
      else if (statusIntegerFamilyComputedByWalk(key, C1_CONTRACT.rollup)) integers.add(key);
      else if (!STATUS_ACTION_RESIDUAL_LAYERS.has(key) && !statusRowOnlyLayer(key) && !SOURCE_LAYERS.has(key)
        && !(alliedClassAura && CLASS_AURA_SOURCE_LAYERS.has(key))) return null;
    }
  }
  // Runtime damage readers apply the team gate themselves. Derived payloads retain their native team-aware compile.
  if ((speeds || skills || hp || mitigation || magical || globals.size || integers.size) && definitions.some(def => def && statusDefinitionLayerNames(def).includes('buff_does_not_layer_onto_opposing_team'))) return null;
  // A status can also switch a retained ability's condition. Derive that dependency normally;
  // unrelated light/equipment conditions are not an excuse to rebuild a marker/speed change.
  const changed = new Set(steps.map(step => upper(step.statusItemId)));
  const conditionDependsOn = (ids: ReadonlySet<string>): boolean => {
    for (const def of [row, ...Object.values(isObj(row.catalog) ? row.catalog : {}), ...Object.values(ctx.catalog ?? {})]) {
      if (!isObj(def)) continue;
      const layers = isObj(def.item_layer_types) ? def.item_layer_types : {};
      for (const key of ['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active']) {
        if (layers[key] === undefined) continue;
        const entries = Object.values(readers.layerEntries(def, key));
        if (entries.some(entry => ids.has(upper(readLayerListEntryUuid(entry))))) return true;
      }
    }
    return false;
  };
  if (conditionDependsOn(changed)) return null;
  const targetId = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
  let target = storedStatusRows(row, 'target'), caster = storedStatusRows(row, 'caster');
  const addIds: string[] = [];
  for (const step of steps) {
    if (step.kind === 'remove') {
      const id = upper(step.statusId);
      if (!target.some(status => upper(readItemField(status, 'status_id')) === id)) return { ok: false, reason: 'input-mismatch:status-missing' };
      target = target.filter(status => upper(readItemField(status, 'status_id')) !== id);
      caster = caster.filter(status => upper(readItemField(status, 'status_id')) !== id);
    } else {
      const id = upper(step.statusId) || (steps.length === 1 ? C1_PROVISIONAL_STATUS_ID : c1ProvisionalStatusChainId(addIds.length));
      const entry = { status_id: id, status_target_id: targetId, status_stack: step.stack ?? 1, status_item_id: upper(step.statusItemId),
        ...(upper(step.casterId) !== targetId ? { status_caster_id: upper(step.casterId) } : {}) };
      target = [entry, ...target];
      if (upper(step.casterId) === targetId) caster = [entry, ...caster];
      addIds.push(id);
    }
  }
  let next = creature;
  const host = integerCompileBorrowedRow(row);
  host.statuses = { ...(isObj(row.statuses) ? row.statuses : {}), target, caster };
  const residueLayers = [...new Set(definitions.flatMap(def => def ? statusDefinitionLayerNames(def) : []))]
    .filter(key => STATUS_RESIDUAL_LAYERS.includes(key));
  const rootFields = compileStatusResidueFields(row, host, { definition: ctx.definition,
    entries: (source, key) => Object.values(readers.layerEntries(source, key)), value: readers.layerValue,
    quantityIds: C1_CONTRACT.rollup.quantityIds }, residueLayers);
  if (integers.size) {
    createIntegerRollupCompiler(deps).delta(host, [...integers]);
    next = (host.computed as Row).creature as Row;
  }
  if (mitigation || magical) {
    const cleaner = createComputeSourceCleaner(deps);
    let priorDamageMitigation: unknown = next.damage_mitigation
      ?? (mitigation ? { total: phpJsonInt(readers.layerValue(row, 'damage_mitigation') ?? 0) } : undefined);
    for (const [index, step] of steps.entries()) {
      const def = definitions[index]!;
      const names = statusDefinitionLayerNames(def);
      const layers: Row = {};
      for (const key of ['damage_mitigation', 'ac_mod_magical']) if (names.includes(key)) layers[key] = readers.layerValue(def, key);
      if (!Object.keys(layers).length) continue;
      const priorAc = isObj(next.ac) ? next.ac : {};
      const priorFlatMagical = phpJsonInt(readers.layerValue({ computed: { creature: next } }, 'ac_mod_magical') ?? 0);
      // The AC owner keeps the scalar, display breakdown and status attribution
      // coherent. The generic integer walk owns none of those source rows.
      const delta = applyStatusAcDelta({ priorAc, priorDamageMitigation, priorFlatMagical,
        layers, statusItemId: upper(step.statusItemId),
        statusName: phpJsonString(readItemField(def, 'item_name') ?? ''), applied: step.kind === 'add',
        stacks: step.kind === 'add' ? step.stack : readStatusRowStack(storedStatusRows(row, 'target')
          .find(status => upper(readItemField(status, 'status_id')) === upper(step.statusId)) ?? {}),
        clean: rows => Object.values(cleaner.clean(rows, row)) as Row[],
        typeOf: id => phpJsonString(readItemField(ctx.definition(id), 'item_type') ?? ''),
        breakdown: rows => computeSourceBreakdown(rows) as Row });
      if (!delta.ok) return delta;
      priorDamageMitigation = delta.damageMitigation;
      next = { ...next, ac: delta.ac };
      if ('ac_mod_magical' in layers) {
        const modifiers = { ...(isObj(next.modifiers) ? next.modifiers : {}) };
        const total = priorFlatMagical + phpJsonInt(delta.ac.total_mod ?? 0) - phpJsonInt(priorAc.total_mod ?? 0);
        if (total) modifiers.ac_mod_magical = total; else delete modifiers.ac_mod_magical;
        if (Object.keys(modifiers).length) next.modifiers = modifiers; else delete next.modifiers;
      }
      if (delta.damageMitigation === undefined) delete next.damage_mitigation;
      else next.damage_mitigation = delta.damageMitigation;
    }
  }

  if (speeds) {
    try {
      const result = compileCreatureSpeeds(host, deps);
      if (!result.ok) return null;
      next = { ...next, speeds: result.speeds };
    } catch { return null; } // Scene-dependent speed inputs still use the established scene-aware compiler.
  }
  if (skills) {
    let projected = next.skills;
    for (const [index, step] of steps.entries()) {
      const def = definitions[index]!;
      if (!statusDefinitionLayerNames(def).some(key => key === 'skill_roll_adv' || key === 'skill_roll_dis')) continue;
      const id = step.kind === 'remove' ? upper(step.statusId) : addIds[steps.slice(0, index).filter(step => step.kind === 'add').length]!;
      // A missing entry can hide cancelling non-status sources. The native path
      // retains its source reconstruction for that case; held entries diff directly.
      const result = applyStatusSkillLines(projected, def, step.kind === 'add', id, C1_CONTRACT.skillEntries,
        readers.layerEntries, () => true);
      if (typeof result === 'string') return null;
      projected = result;
    }
    countStatusCompileWork('skills', row);
    next = { ...next, skills: projected };
  }
  if (globals.size) {
    const integers = createStatusIntegerCompiler(deps);
    let modifiers = isObj(next.modifiers) ? next.modifiers : undefined;
    let sources = isObj(next.modifier_sources) ? next.modifier_sources : undefined;
    const layers: Row = {};
    for (const layer of globals) {
      const before = integers.contribution(row, layer), after = integers.contribution(host, layer);
      if (layer === 'skill_rolls_mod') { layers[layer] = after.total - before.total; continue; }
      layers[layer] = 1; // re-attribute from held post-mutation totals, including zero/elided totals
      const total = phpJsonInt(modifiers?.[layer] ?? 0) - before.total + after.total;
      const oldRows = sources?.[layer];
      const previous = oldRows !== null && typeof oldRows === 'object' ? Object.values(oldRows) : [];
      const retained = previous.filter(source => !isObj(source) || (source.category ?? source.kind) !== 'status_effect');
      const changed = after.sources.map(source => previous.find(prior => isObj(prior)
        && Object.keys(prior).length === Object.keys(source).length
        && Object.entries(source).every(([key, value]) => prior[key] === value)) ?? source);
      const family = [...changed, ...retained];
      modifiers = { ...modifiers }; sources = { ...sources };
      if (total) modifiers[layer] = total; else delete modifiers[layer];
      if (family.length) sources[layer] = family; else delete sources[layer];
    }
    const names = createAdvDisSourceReader({ ...deps, queryDefinition: ctx.definition,
      resolveInventoryIdentity: createInventoryTemplateIdentityResolver(deps.queryTemplateDefinition) });
    // A pre-native zero-net non-status pair can be omitted from the held
    // breakdown. Preserve the existing named hidden-lines boundary rather than
    // accepting it while silently losing those lines in the new fast path.
    const nonStatus: Row[] = [host];
    const abilityBuckets = isObj(creature.abilities) ? creature.abilities : {};
    for (const bucket of ['passive_ability', 'feat']) for (const entry of Object.values(abilityBuckets[bucket] ?? {})) {
      if (isObj(entry) && entry.passive_conditions_active === false) continue;
      const def = ctx.definition(upper(readLayerListEntryUuid(entry)));
      if (def && !statusDefinitionLayerNames(def).some(key => key.includes('trigger'))) nonStatus.push(def);
    }
    const collectEquipped = (row: Row, key: 'inventory' | 'inventory_in_container'): void => {
      for (const instance of readInventoryRows(row, key)) {
        if (readItemUuidSlot(readInventoryField(instance, 'equipped') ?? '')) {
          const def = ctx.definition(phpJsonString(readInventoryField(instance, 'inventory_item_id')));
          if (def) nonStatus.push(def);
        }
        collectEquipped(instance, 'inventory_in_container');
      }
    };
    collectEquipped(host, 'inventory');
    const adv = nonStatus.flatMap(source => Object.values(readers.layerEntries(source, 'skill_roll_adv')));
    const dis = nonStatus.flatMap(source => Object.values(readers.layerEntries(source, 'skill_roll_dis')));
    if (globals.has('skill_rolls_adv') || globals.has('skill_rolls_dis')) for (const skill of C1_CONTRACT.rollup.skillEntries) {
      const entry = isObj(next.skills) ? next.skills[skill.variable_name] : undefined;
      if (isObj(entry) && isObj(entry.adv_dis_breakdown)) continue;
      if (adv.some(value => layerEntryTargetsSkill(value, skill.item_id, skill.variable_name))
        && dis.some(value => layerEntryTargetsSkill(value, skill.item_id, skill.variable_name)))
        return { ok: false, reason: `status-skills-global:hidden-lines:${skill.variable_name}` };
    }
    const result = applyStatusSkillGlobals({ skills: isObj(next.skills) ? next.skills : undefined, modifiers,
      modifierSources: sources, layers, applied: true, statusName: '', statusRowId: '',
      skillEntries: C1_CONTRACT.rollup.skillEntries,
      heldStatusIds: new Set(target.map(status => upper(readItemField(status, 'status_id')))),
      sourceNames: layer => names.sources(host, layer).map(source => phpJsonString(source.name ?? '')),
      sourceLabel: id => {
        const status = Object.values(names.statuses(host)).find(row => isObj(row) && upper(readItemField(row, 'status_id')) === id);
        return isObj(status) ? phpJsonString(readItemField(status, 'item_name', deps) ?? '') : undefined;
      } });
    if (!result.ok) return result;
    next = { ...next, skills: result.skills };
    if (modifiers && Object.keys(modifiers).length) next.modifiers = modifiers; else delete next.modifiers;
    if (sources && Object.keys(sources).length) next.modifier_sources = sources; else delete next.modifier_sources;
    countStatusCompileWork('skills', row);
  }
  if (hp) {
    countStatusCompileWork('vitality', row);
    const compiler = createVitalityCompiler({ ...deps, ...C1_CONTRACT.vitality,
      resolveInventoryIdentity: () => null, resolveShell: () => null });
    // The vitality builder owns this small input, rather than cloning the member,
    // its catalog, inventory, skills and all other derived clusters twice.
    const result = compiler.compile({ items: { item_type: carrier }, statuses: host.statuses, computed: { vitality } }).row as Row;
    const statusId = (v: Row): string => { const status = readItemField(v, 'v_status');
      return upper(isObj(status) ? readItemField(status, 'item_id') : status); };
    const previous = statusId(vitality);
    vitality = (result.computed as Row).vitality as Row;
    if (isObj(vitality.v_status)) vitality.v_status = readItemField(vitality.v_status, 'item_id');
    if (previous !== statusId(vitality) && conditionDependsOn(new Set([previous, statusId(vitality)]))) return null;
  }
  if (carrier === 'creature' && definitions.some(def => !surfaceStatusRuntimeOnly(def)
    && !(def && statusDefinitionLayerNames(def).some(key => STATUS_ACTION_RESIDUAL_LAYERS.has(key))
      && statusDefinitionLayerNames(def).every(key => STATUS_ACTION_RESIDUAL_LAYERS.has(key) || statusRowOnlyLayer(key))))
    && (!target.length || !storedStatusRows(row, 'target').length)) {
    countStatusCompileWork('opportunity', row);
    try {
      const post = { ...host, computed: { ...computed, creature: next, vitality } };
      const itemDeps = { ...deps, resolveInventoryIdentity: createInventoryTemplateIdentityResolver(deps.queryTemplateDefinition) };
      const oa = opportunityAttackInput(post, {
        itemField: (r, key) => readItemField(r, key, itemDeps), layerValue: readers.layerValue,
        layerEntries: (r, key) => Object.values(readers.layerEntries(r, key)),
        scalarSlot: readItemScalarLayerSlot, entryUuid: entry => typeof entry === 'string' ? entry : readLayerListEntryUuid(entry),
        definition: ctx.definition,
        // Admitted status layers change no equipment. Reuse its held counts;
        // opportunityAttackInput reads only these count/selection slots.
        equippedCounts: r => ({ ...r,
          // Lean equipped clusters omit zero counts; the native packet serializes
          // those zeros too (through scalarSlot), including on unarmed monsters.
          count_both_hands_weapon_equipped: 0, count_main_hand_weapon_equipped: 0, count_off_hand_weapon_equipped: 0,
          count_main_hand_natural_weapon_equipped: 0, count_off_hand_natural_weapon_equipped: 0,
          ...(isObj(next.equipped) ? next.equipped : {}) }),
      }, false);
      if (!('packet' in oa)) return null;
      next = { ...next, opportunity_attack: oa.packet };
    } catch { return null; } // Unresolved OA inputs use the existing compiler for this member only.
  }
  const catalog = isObj(row.catalog) ? row.catalog : ctx.catalog ?? null, additions: Row = {};
  for (const [index, step] of steps.entries()) if (step.kind === 'add' && !catalog?.[upper(step.statusItemId)])
    additions[upper(step.statusItemId)] = definitions[index]!;
  return { ok: true, creature: next, vitality, rootScalars: {}, rootFields,
    catalog: Object.keys(additions).length ? { ...catalog, ...additions } : catalog, addIds };
}

